import { useState, useEffect } from 'react';

export const usePWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = window.navigator.standalone === true;
      const isInstalled = isStandalone || isIOSStandalone;
      setIsInstalled(isInstalled);
      console.log('PWA: Installation check:', { isStandalone, isIOSStandalone, isInstalled });
    };

    checkIfInstalled();

    // Handle install prompt - CRITICAL FIX for 2025 PWA standards
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA: beforeinstallprompt event fired - capturing event');
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('PWA: Deferred prompt captured and stored successfully');
    };

    // Handle app installed
    const handleAppInstalled = (e) => {
      console.log('PWA: App was installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Handle online/offline status
    const handleOnline = () => {
      console.log('PWA: App came online');
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      console.log('PWA: App went offline');
      setIsOnline(false);
    };

    // Add event listeners IMMEDIATELY when component mounts
    console.log('PWA: Adding event listeners...');
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Debug: Check if PWA criteria are met
    if ('serviceWorker' in navigator) {
      console.log('PWA: Service Worker supported');
    }
    
    // Check for manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      console.log('PWA: Manifest found:', manifestLink.href);
    } else {
      console.warn('PWA: No manifest link found');
    }

    // Cleanup
    return () => {
      console.log('PWA: Removing event listeners...');
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const installApp = async () => {
    console.log('PWA: Install requested. Deferred prompt available:', !!deferredPrompt);
    
    if (!deferredPrompt) {
      console.error('PWA: No install prompt available. This could be because:');
      console.error('- App is already installed');
      console.error('- Browser doesn\'t support PWA installation');
      console.error('- beforeinstallprompt event hasn\'t fired yet');
      console.error('- PWA criteria not met (HTTPS, manifest, service worker)');
      return false;
    }

    try {
      console.log('PWA: Showing install prompt...');
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log('PWA: User choice:', outcome);
      
      if (outcome === 'accepted') {
        console.log('PWA: User accepted the install prompt');
        setIsInstallable(false);
        setDeferredPrompt(null);
        return true;
      } else {
        console.log('PWA: User dismissed the install prompt');
        // Keep the deferred prompt for future use
        return false;
      }
    } catch (error) {
      console.error('PWA: Error during installation:', error);
      return false;
    }
  };

  const registerForNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('PWA: Push messaging is not supported');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('PWA: Notification permission granted');
        
        // Subscribe to push notifications
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: 'your-vapid-public-key' // Replace with actual VAPID key
        });
        
        // Send subscription to your backend
        // await sendSubscriptionToBackend(subscription);
        
        return true;
      } else {
        console.log('PWA: Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('PWA: Error registering for notifications:', error);
      return false;
    }
  };

  const shareContent = async (shareData) => {
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return true;
      } catch (error) {
        console.error('PWA: Error sharing content:', error);
        return false;
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(shareData.url || shareData.text);
          return true;
        } catch (error) {
          console.error('PWA: Error copying to clipboard:', error);
          return false;
        }
      }
      return false;
    }
  };

  return {
    isInstalled,
    isInstallable,
    isOnline,
    installApp,
    registerForNotifications,
    shareContent
  };
};